import axios from 'axios';
import { clearStoredTokens } from '../token';
import { safeSessionStorage } from '@/shared/lib/storage';

const COGNITO_CONFIG = {
    DOMAIN: import.meta.env.VITE_COGNITO_DOMAIN,
    CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID,
    REDIRECT_URI: import.meta.env.VITE_COGNITO_REDIRECT_URI,
    LOGOUT_URI: import.meta.env.VITE_COGNITO_LOGOUT_URI,
} as const;

const CODE_VERIFIER_KEY = 'cognito_code_verifier';

export type TokenResponse = {
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_in: number;
    token_type: string;
};

type CallbackResult = { ok: true; tokens: TokenResponse } | { ok: false; error: string };
type RefreshResult = { ok: true; tokens: { access_token: string; id_token: string } } | { ok: false; error: string };

// MEMO: api/clientのaxiosInstanceを使うと、インターセプターが絡んで循環参照になるため専用インスタンスを作成
const cognitoClient = axios.create({
    baseURL: `https://${COGNITO_CONFIG.DOMAIN}`,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
});

const base64UrlEncode = (buffer: Uint8Array): string => {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * PKCE (Proof Key for Code Exchange) 実装
 * @see https://advancedweb.hu/how-to-secure-the-cognito-login-flow-with-a-state-nonce-and-pkce/
 */
const generateCodeVerifier = (): string => {
    const array = crypto.getRandomValues(new Uint8Array(32));
    return base64UrlEncode(array);
};

const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
};

export const redirectToLogin = async (): Promise<void> => {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    safeSessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: COGNITO_CONFIG.CLIENT_ID,
        redirect_uri: COGNITO_CONFIG.REDIRECT_URI,
        scope: 'openid email phone',
        code_challenge_method: 'S256',
        code_challenge: codeChallenge,
    });

    const loginUrl = `https://${COGNITO_CONFIG.DOMAIN}/login?${params.toString()}`;
    window.location.href = loginUrl;
};

export const logout = (): void => {
    clearStoredTokens();

    const params = new URLSearchParams({
        client_id: COGNITO_CONFIG.CLIENT_ID,
        logout_uri: COGNITO_CONFIG.LOGOUT_URI,
    });

    const logoutUrl = `https://${COGNITO_CONFIG.DOMAIN}/logout?${params.toString()}`;
    window.location.href = logoutUrl;
};

const getAuthCodeFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code');
};

const getErrorFromUrl = (): string => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    if (!error) return '';
    return urlParams.get('error_description') || error;
};

const exchangeCodeForTokens = async (code: string): Promise<CallbackResult> => {
    const codeVerifier = safeSessionStorage.getItem(CODE_VERIFIER_KEY);

    if (!codeVerifier) {
        return { ok: false, error: 'code_verifierが見つかりません' };
    }

    const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: COGNITO_CONFIG.CLIENT_ID,
        code: code,
        redirect_uri: COGNITO_CONFIG.REDIRECT_URI,
        code_verifier: codeVerifier,
    });

    const response = await fetch(`https://${COGNITO_CONFIG.DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
    });

    if (!response.ok) {
        const errorData = await response.json();
        return { ok: false, error: errorData.error_description || 'トークン取得に失敗しました' };
    }

    safeSessionStorage.removeItem(CODE_VERIFIER_KEY);

    return { ok: true, tokens: await response.json() };
};

export const handleAuthCallback = async (): Promise<CallbackResult> => {
    const urlError = getErrorFromUrl();
    const code = getAuthCodeFromUrl();

    if (urlError) {
        return { ok: false, error: urlError };
    }
    if (!code) {
        return { ok: false, error: '認証コードが見つかりません' };
    }

    return await exchangeCodeForTokens(code);
};

export const refreshTokens = async (refreshToken: string): Promise<RefreshResult> => {
    const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: COGNITO_CONFIG.CLIENT_ID,
        refresh_token: refreshToken,
    });

    try {
        const response = await cognitoClient.post('/oauth2/token', params.toString());
        return { ok: true, tokens: response.data };
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { ok: false, error: error.response?.data?.error_description || 'トークン更新に失敗しました' };
        }
        return { ok: false, error: 'トークン更新に失敗しました' };
    }
};
